/**
 * Menu Planning API Service
 * 
 * Service layer for interacting with the menu planning backend APIs.
 */

import { api } from './api';

export const menuPlanningService = {
  /**
   * Check if menu planning feature is enabled for current user
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object>} Feature status
   */
  async getFeatureStatus(token) {
    const { data } = await api.get('/menu-planning/status', {
      headers: { Authorization: `Bearer ${token}` }
    });
    return data;
  },

  /**
   * Get recipe segments for a specific recipe
   * @param {number} recipeId - Recipe ID
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object>} Steps with segments and totals
   */
  async getRecipeSegments(recipeId, token) {
    const { data } = await api.get(`/recipes/${recipeId}/segments`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return data;
  },

  /**
   * Update recipe segments
   * @param {number} recipeId - Recipe ID
   * @param {Array} stepsWithSegments - Steps with segments
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object>} Updated segments
   */
  async updateRecipeSegments(recipeId, stepsWithSegments, token) {
    const { data } = await api.put(`/recipes/${recipeId}/segments`, { 
      steps: stepsWithSegments 
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return data;
  },

  /**
   * Generate schedule for multiple recipes
   * @param {Array<number>} recipeIds - Array of recipe IDs
   * @param {Object} options - Scheduling options
   * @param {string} options.priority - 'shortestActiveFirst' or 'recipeOrder'
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object>} Schedule result with segments and makespan
   */
  async scheduleRecipes(recipeIds, options = {}, token) {
    const { data } = await api.post('/menu-planning/schedule', { 
      recipe_ids: recipeIds, 
      options 
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return data;
  },

  /**
   * Validate a user-modified schedule
   * @param {Array} scheduledSegments - Array of scheduled segments
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object>} Validation result
   */
  async validateSchedule(scheduledSegments, token) {
    try {
      const { data } = await api.post('/menu-planning/validate', { 
        scheduled_segments: scheduledSegments 
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return data;
    } catch (error) {
      // If validation endpoint returns 409 (conflict), that's not an error
      if (error.response && error.response.status === 409) {
        return error.response.data;
      }
      throw error;
    }
  },

  /**
   * Save a menu plan
   * @param {string} name - Plan name
   * @param {Array<number>} recipeIds - Recipe IDs
   * @param {Object} scheduleData - Schedule data
   * @param {string} token - Firebase ID token
   * @param {string} occasionId - Optional occasion ID
   * @returns {Promise<Object>} Saved menu plan
   */
  async saveMenuPlan(name, recipeIds, scheduleData, token, occasionId = null) {
    const { data } = await api.post('/menu-planning/plans', { 
      name, 
      recipe_ids: recipeIds, 
      schedule_data: scheduleData,
      occasion_id: occasionId
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return data;
  },

  /**
   * Get user's saved menu plans
   * @param {string} token - Firebase ID token
   * @returns {Promise<Array>} Array of menu plans
   */
  async getMenuPlans(token) {
    const { data } = await api.get('/menu-planning/plans', {
      headers: { Authorization: `Bearer ${token}` }
    });
    return data;
  },

  /**
   * Get a menu plan by ID
   * @param {string} planId - Menu plan ID
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object>} Menu plan
   */
  async getMenuPlan(planId, token) {
    const { data } = await api.get(`/menu-planning/plans/${planId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return data;
  },

  /**
   * Get menu plan by occasion ID
   * @param {string} occasionId - Occasion ID
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object|null>} Menu plan or null if not found
   */
  async getMenuPlanByOccasion(occasionId, token) {
    try {
      const { data } = await api.get(`/menu-planning/plans/by-occasion/${occasionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return data;
    } catch (error) {
      if (error.response && error.response.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Delete a menu plan
   * @param {string} planId - Plan ID
   * @param {string} token - Firebase ID token
   * @returns {Promise<Object>} Success result
   */
  async deleteMenuPlan(planId, token) {
    const { data } = await api.delete(`/menu-planning/plans/${planId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return data;
  }
};

export default menuPlanningService;

